import { PostEditorBulkImportGlobalSetAction, PostEditorReducerFunction } from '../PostEditorActions';
import { ImmutabilityContext } from '../effects/customImmutabilityContext';
import { GroupPostAttributesType, PostEditorLink, PostGroupsType } from '../../../../models/post-editor';
import { SelectedSocialProfileValue } from '../../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import {
  LinkDestination,
  MediaType,
  PostFeedType,
  PostPublishMethod,
  PostTiming,
  UtmInput
} from '../../../../models/graphql';
import { SocialProfileWithBoardsAndMentionType } from '../../helpers/mapSelectedProfilesValues';
import { MaxIgVideoLengthValue } from '../../../../constants/media';
import { IgProfilesTypeFn } from '../../InstagramPostTypeBlock/InstagramPostTypeBlock.controller';
import { EditorRefType } from '../PostEditorProvider';
import { getUrlParams } from '../../../../helpers/getUrlParams';
import { makeFinalLink } from './link.reducer';
import { updateLink } from '../../TextEditor/helpers/withLinks';
import { validator } from '../../validators/helpers/validator';
import { ErrorMessageSeverity } from '../../../../constants/form';

const mapLinks = (
  currentLink: PostEditorLink | null,
  linkDestination: LinkDestination | undefined,
  keepExistingUtm: boolean | undefined,
  utmTracking: UtmInput | undefined | null,
  useBitly: boolean | undefined,
  editors: EditorRefType[]
): PostEditorLink | null => {
  if (!currentLink) return null;

  const { update: updateImmutable } = ImmutabilityContext;
  const initialUrlized = getUrlParams(currentLink.initialUrl);
  const hasRedirects =
    currentLink.linkMetadata && initialUrlized && initialUrlized.href !== currentLink.linkMetadata.expandedUrl;
  const newDestination = linkDestination
    ? linkDestination === LinkDestination.EXPANDED && hasRedirects
      ? LinkDestination.EXPANDED
      : LinkDestination.INITIAL
    : currentLink.destination;
  const newRemoveUtm =
    keepExistingUtm !== undefined &&
    !(newDestination === LinkDestination.INITIAL && currentLink.linkMetadata?.isBitlink)
      ? !keepExistingUtm
      : currentLink.removeUtm;
  const newAddUtm =
    utmTracking !== undefined && !(newDestination === LinkDestination.INITIAL && currentLink.linkMetadata?.isBitlink)
      ? utmTracking !== null
      : currentLink.addUtm;
  const newUseBitly =
    useBitly !== undefined && !(newDestination === LinkDestination.INITIAL && currentLink.linkMetadata?.isBitlink)
      ? useBitly
      : currentLink.useBitly;
  const { url: _, ...baseLinkSettings } = currentLink;
  baseLinkSettings.destination = newDestination;
  if (utmTracking) {
    baseLinkSettings.utmTracking = utmTracking;
  }
  baseLinkSettings.removeUtm = newRemoveUtm;
  baseLinkSettings.addUtm = newAddUtm;
  baseLinkSettings.useBitly = newUseBitly;
  const updatedLink = updateImmutable<PostEditorLink>(currentLink, {
    destination: current => (linkDestination ? newDestination : current),
    addUtm: { $set: newAddUtm },
    utmTracking: current => utmTracking ?? current,
    useBitly: { $set: newUseBitly },
    url: { $set: makeFinalLink(baseLinkSettings) },
    removeUtm: { $set: newRemoveUtm }
  });
  editors.forEach(editor => {
    updateLink(editors, editor.editor, updatedLink);
  });
  return updatedLink;
};

const updatePostFeedTypeAndPublishMethod = (
  group: PostGroupsType,
  profiles: SelectedSocialProfileValue[] | undefined,
  postFeedType: PostFeedType | undefined,
  publishMethod: PostPublishMethod | null | undefined,
  post: GroupPostAttributesType,
  availableProfiles: SocialProfileWithBoardsAndMentionType[],
  customizeContent: boolean
): { type: PostFeedType | null; method: PostPublishMethod | null } => {
  if (customizeContent) {
    const { customizedProfileId } = post;
    const igProfiles = availableProfiles.filter(
      p => p.__typename === 'InstagramSocialProfile' && p.id === customizedProfileId
    );
    const igProfilesType = IgProfilesTypeFn(igProfiles);
    const videos = post.content.media.filter(m => m.type === MediaType.VIDEO);
    const maxVideoLength = Math.max(...videos.map(v => v.length || 0));
    const igType = postFeedType && igProfiles.length ? postFeedType : post.content.postFeedType;
    const hasLongVideo = maxVideoLength >= MaxIgVideoLengthValue && igType === PostFeedType.FEED;
    const canPublishDirectly = igProfilesType !== 'personal' && !hasLongVideo;
    return {
      type: igProfiles.length && postFeedType ? postFeedType : post.content.postFeedType,
      method:
        igProfiles.length && publishMethod
          ? publishMethod === PostPublishMethod.DIRECT && canPublishDirectly
            ? publishMethod
            : PostPublishMethod.MANUAL
          : post.content.publishMethod
    };
  } else {
    const profilesForCheck = profiles ? profiles : group.profiles;
    const selectedProfilesIds = profilesForCheck.map(p => p.profileId);
    const igProfiles = availableProfiles.filter(
      p => p.__typename === 'InstagramSocialProfile' && selectedProfilesIds.includes(p.id)
    );
    const igProfilesType = IgProfilesTypeFn(igProfiles);
    const videos = group.groupPostAttributes.content.media.filter(m => m.type === MediaType.VIDEO);
    const maxVideoLength = Math.max(...videos.map(v => v.length || 0));
    const igType = postFeedType && igProfiles.length ? postFeedType : group.groupPostAttributes.content.postFeedType;
    const hasLongVideo = maxVideoLength >= MaxIgVideoLengthValue && igType === PostFeedType.FEED;
    const canPublishDirectly = igProfilesType !== 'personal' && !hasLongVideo;
    return {
      type: igProfiles.length && postFeedType ? postFeedType : group.groupPostAttributes.content.postFeedType,
      method:
        igProfiles.length && publishMethod
          ? publishMethod === PostPublishMethod.DIRECT && canPublishDirectly
            ? publishMethod
            : PostPublishMethod.MANUAL
          : group.groupPostAttributes.content.publishMethod
    };
  }
};

export const bulkImportGlobalSetReducer: PostEditorReducerFunction<PostEditorBulkImportGlobalSetAction['payload']> = (
  state,
  payload
) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const {
    availableProfiles,
    postStatus,
    profiles,
    category,
    postFeedType,
    publishMethod,
    linkDestination,
    utmTracking,
    useBitly,
    keepExistingUtm,
    queuePosition,
    evergreen,
    editors
  } = payload;
  return updateImmutable(state, {
    postGroups: (currentGroups: PostGroupsType[]) =>
      currentGroups.map((group, postGroupIndex) => {
        const { customizeSchedulePerProfile, customizeContentPerProfile } = group;
        let posts: GroupPostAttributesType[] = [];
        // no new profiles, so just update existing ones
        const { posts: currentPosts } = group;
        const currentlySelectedProfileIds = currentPosts.flatMap(p => p.customizedProfileId ?? []);
        if (!profiles && (customizeSchedulePerProfile || customizeContentPerProfile)) {
          posts = currentPosts.map(p => {
            const { type, method } = updatePostFeedTypeAndPublishMethod(
              group,
              profiles,
              postFeedType,
              publishMethod,
              p,
              availableProfiles,
              true
            );
            return updateImmutable<GroupPostAttributesType>(p, {
              postStatus: current => postStatus ?? current,
              content: {
                link: current =>
                  mapLinks(
                    current,
                    linkDestination,
                    keepExistingUtm,
                    utmTracking,
                    useBitly,
                    editors.current[postGroupIndex]
                  ),
                postFeedType: current => (postFeedType && type ? type : current),
                publishMethod: current => (publishMethod ? method : current)
              },
              scheduling: {
                postTiming: current => (queuePosition ? PostTiming.QUEUE : current),
                evergreen: current => evergreen ?? current,
                queuePosition: current => queuePosition ?? current
              }
            });
          });
        } else if (profiles && (customizeSchedulePerProfile || customizeContentPerProfile)) {
          // we do have profiles passed in.
          posts = profiles.map(p => {
            if (currentlySelectedProfileIds.includes(p.profileId)) {
              // we have that profile customized already, so just use that and apply values from bulk actions
              const currentPost = currentPosts.find(cp => cp.customizedProfileId === p.profileId);
              if (!currentPost) throw new Error('CustomizedProfileId prop for requested post is null.');
              const { type, method } = updatePostFeedTypeAndPublishMethod(
                group,
                profiles,
                postFeedType,
                publishMethod,
                currentPost,
                availableProfiles,
                true
              );
              return updateImmutable<GroupPostAttributesType>(currentPost, {
                postStatus: current => postStatus ?? current,
                content: {
                  link: current =>
                    mapLinks(
                      current,
                      linkDestination,
                      keepExistingUtm,
                      utmTracking,
                      useBitly,
                      editors.current[postGroupIndex]
                    ),
                  postFeedType: current => (postFeedType && type ? type : current),
                  publishMethod: current => (publishMethod ? method : current)
                },
                scheduling: {
                  postTiming: current => (queuePosition ? PostTiming.QUEUE : current),
                  evergreen: current => evergreen ?? current,
                  queuePosition: current => queuePosition ?? current
                }
              });
            } else {
              // we don't have that profile in list, so we just need to copy current state from the groupAttributes, but we need first to update the group
              const currentPost = updateImmutable<GroupPostAttributesType>(group.groupPostAttributes, {
                customizedProfileId: { $set: p.profileId },
                postStatus: current => postStatus ?? current,
                content: {
                  link: current =>
                    mapLinks(
                      current,
                      linkDestination,
                      keepExistingUtm,
                      utmTracking,
                      useBitly,
                      editors.current[postGroupIndex]
                    )
                },
                scheduling: {
                  postTiming: current => (queuePosition ? PostTiming.QUEUE : current),
                  evergreen: current => evergreen ?? current,
                  queuePosition: current => queuePosition ?? current
                }
              });
              const { type, method } = updatePostFeedTypeAndPublishMethod(
                group,
                profiles,
                postFeedType,
                publishMethod,
                currentPost,
                availableProfiles,
                true
              );
              return updateImmutable<GroupPostAttributesType>(currentPost, {
                postStatus: current => postStatus ?? current,
                content: {
                  link: current =>
                    mapLinks(
                      current,
                      linkDestination,
                      keepExistingUtm,
                      utmTracking,
                      useBitly,
                      editors.current[postGroupIndex]
                    ),
                  postFeedType: current => (postFeedType && type ? type : current),
                  publishMethod: current => (publishMethod ? method : current)
                },
                scheduling: {
                  postTiming: current => (queuePosition ? PostTiming.QUEUE : current),
                  evergreen: current => evergreen ?? current,
                  queuePosition: current => queuePosition ?? current
                }
              });
            }
          });
        }
        const { type, method } = updatePostFeedTypeAndPublishMethod(
          group,
          profiles,
          postFeedType,
          publishMethod,
          group.groupPostAttributes,
          availableProfiles,
          false
        );
        const result = updateImmutable<PostGroupsType>(group, {
          groupPostAttributes: {
            postStatus: current => postStatus ?? current,
            content: {
              link: current =>
                mapLinks(
                  current,
                  linkDestination,
                  keepExistingUtm,
                  utmTracking,
                  useBitly,
                  editors.current[postGroupIndex]
                ),
              postFeedType: current => (postFeedType && type ? type : current),
              publishMethod: current => (publishMethod ? method : current)
            },
            scheduling: {
              postTiming: current => (queuePosition ? PostTiming.QUEUE : current),
              evergreen: current => evergreen ?? current,
              queuePosition: current => queuePosition ?? current
            }
          },
          activeCustomizeIndex: current => (profiles ? null : current),
          posts: { $set: posts },
          categoryId: current => category ?? current,
          profiles: current => profiles ?? current
        });
        const errors = validator(result, ErrorMessageSeverity.warning, state.postGroups[postGroupIndex].errors);
        return updateImmutable(result, {
          errors: {
            $errorMerge: errors
          }
        });
      })
  });
};
